import { useNavigate } from "react-router-dom";
import { formatDate, formatNumber, formatOrdinal, MEASURE_COLOR_LIST, PATH_INFO } from "../utils/helper";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { sampleDefault, sampleImages, SamplesContext, SamplesResponseType, UpdateSamplesContext } from "../../context/samplesContext";
import checkboxIcon from '../../assets/images/icons/checked_checkbox.svg'
import uncheckIcon from '../../assets/images/icons/uncheck_checkbox.svg'
import infoIcon from '../../assets/images/icons/info-outline.svg'
import checkIcon from '../../assets/images/icons/checkGreen.svg'
import { Modal } from "../../components/Modal";
import { useTableContext } from "../../context/tableContext";
import InfiniteScroll from "react-infinite-scroll-component";
import ReactLoading from "react-loading";
import { ImageWithLoading } from "../../components/image/ImageWithLoading";
import { loadPreviewImage } from "../utils/loadPreviewImage";

export function QrSearch() {
  const { idSearchSample } = useContext(UpdateSamplesContext)
  const navigate = useNavigate()
  const { setColorMap, setIndexMap } = useTableContext()
  const [sampleInfo, setSampleInfo] = useState<SamplesResponseType>(sampleDefault)
  const [sampleId, setSampleId] = useState<string>('')
  const [isMapReady, setIsMapReady] = useState(false)

  useEffect(() => {
    const fetchSampleInfo = async () => {
      if (!sampleId) return
      setIsMapReady(false) // 新しいサンプルを読み込む前にリセット
      const data = await idSearchSample(sampleId, true, false)
      if (data) {
        setSampleInfo(data)
      }
    }
    fetchSampleInfo()
  }, [sampleId, idSearchSample])

  useEffect(() => {
    if (sampleInfo.id === '') return // 初期状態では何もしない
    const activeMeasurements = sampleInfo.sampleMeasurements
      .filter(sm => !sm.is_deleted)
      .sort((a, b) => a.measure_index - b.measure_index)

    const cm = new Map<number, string>()
    const im = new Map<number, number>()
    activeMeasurements.forEach((row, i) => {
      cm.set(row.measure_index, MEASURE_COLOR_LIST[i]?.color ?? 'black')
      im.set(row.measure_index, i + 1)
    })
    setColorMap(cm)
    setIndexMap(im)
    setIsMapReady(true) // マップの準備完了を通知
  }, [sampleInfo, setColorMap, setIndexMap])

  return (
    <div>
      <div className='flex items-center w-full'>
        <div className='w-2 h-[35px] rounded-[3px] bg-black mr-[5px]' ></div>
        <span className='text-[#3E3E3E] text-[18px]'>
          QR 画像
        </span>
      </div>
      <div className="flex flex-col items-center justify-center w-full h-full gap-[12px] mt-[12px] desktop-sm:flex-row desktop-sm:items-start">
        <SamplesTable setSampleId={setSampleId} />
        <div className="flex justify-center gap-[12px]">
          <QrAndUrl sampleInfo={sampleInfo} />
          <SamplePhotos sampleInfo={sampleInfo} isMapReady={isMapReady} />
        </div>
      </div>
      <div className="w-full flex justify-end mt-[10px]">
        <button onClick={() => navigate(PATH_INFO.HOME.PATH)} className="bg-black w-[280px] h-[47px] rounded-[50px]">
          <span className="text-white">検寸画面に戻る</span>
        </button>
      </div>
    </div>
  )
}

const QR_SEARCH_SCROLL_ID = "qr-search-scroll-container"

function SamplesTable(props: { setSampleId: (id: string) => void }) {
  const { setSampleId } = props
  const { samples, samplesMeta } = useContext(SamplesContext)
  const { loadMoreSamples } = useContext(UpdateSamplesContext)

  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [sortedSamples, setSortedSamples] = useState(samples)

  useEffect(() => {
    if (Array.isArray(samples)) {
      const sorted = samples.slice().sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        if (sortOrder === 'asc') {
          return dateA - dateB
        }
        return dateB - dateA
      })
      setSortedSamples(sorted)
    }
  }, [samples, sortOrder])

  return (
    <div className="min-w-[590px] min-h-[398px] desktop-sm:min-h-[650px] w-full max-w-[930px] desktop-sm:w-fit desktop-sm:max-w-fit">
      <div className="flex items-center justify-end space-x-[8px] mb-[10px]">
        <label className="flex items-center space-x-[8px] ">
          <input
            type="radio"
            name="sort"
            value="asc"
            checked={sortOrder === 'asc'}
            onChange={() => setSortOrder('asc')}
            className="w-[26px] h-[26px] accent-[#545899]"
          />
          <span>撮影日順(昇順)</span>
        </label>
        <label className="flex items-center space-x-[8px]">
          <input
            type="radio"
            name="sort"
            value="desc"
            checked={sortOrder === 'desc'}
            onChange={() => setSortOrder('desc')}
            className="w-[26px] h-[26px] accent-[#545899]"
          />
          <span>撮影日順(降順)</span>
        </label>
      </div>
      <div
        id={QR_SEARCH_SCROLL_ID}
        className='max-h-[398px] bg-white border min-h-[398px] h-full overflow-y-scroll desktop-sm:min-h-[650px] desktop-sm:h-[650px]'
      >
        <InfiniteScroll
          dataLength={sortedSamples?.length ?? 0}
          next={loadMoreSamples}
          hasMore={samplesMeta.hasMore}
          loader={
            <div className='flex items-center justify-center py-3 text-center text-xs text-gray-600'>
              <ReactLoading type='spin' color='#666666' height={16} width={16} className='inline-block mr-2' />
              <span>サンプル一覧を読み込み中です…</span>
            </div>
          }
          endMessage={<div className='py-3 text-center text-xs text-gray-600'>読み込めるサンプルはすべて表示済みです。</div>}
          scrollableTarget={QR_SEARCH_SCROLL_ID}
          scrollThreshold='200px'
          style={{ overflow: 'visible' }}
        >
          <table className='bg-white w-full h-full text-[#666666] desktop-sm:max-w-[590px]'>
            <thead className='sticky top-0'>
              <tr className='[&>th]:bg-[#3E3E3E] [&>th]:text-white [&>th]:font-normal [&>th]:text-[14px] [&>th]:border'>
                <th className='sticky top-0 w-[88px]'></th>
                <th className='sticky top-0 w-[145px]'>検寸日時</th>
                <th className='sticky top-0 w-[145px]'>品番</th>
                <th className='sticky top-0 w-[173px]'>サンプルNo</th>
                <th className='sticky top-0 w-[173px]'>サンプル状況</th>
                <th className='sticky top-0 w-[100px]'>サイズ</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(samples) && sortedSamples?.map((sample, index) => (
                <tr key={index}>
                  <td className='border'>
                    <div className='flex items-center justify-center w-full h-full'>
                      <label className='cursor-pointer'>
                        <input
                          type="radio"
                          name='check'
                          className='hidden peer'
                          onChange={() => setSampleId(sample.id)}
                        />
                        <img src={uncheckIcon} alt='uncheck' className='block peer-checked:hidden' />
                        <img src={checkboxIcon} alt='checked' className='hidden peer-checked:block' />
                      </label>
                    </div>
                  </td>
                  <td className='border text-[14px] text-center'>{formatDate(sample.created_at)}</td>
                  <td className='border text-[14px] text-center'>{sample.part_number || '-'}</td>
                  <td className='border text-[14px] text-center'>{sample.sample_number || '-'}</td>
                  <td className='border text-[14px] text-center'>{sample.kind === 'final' ? 'FINAL' : sample.time !== 0 ? formatOrdinal(sample.time) : '-'}</td>
                  <td className='border text-[16px] text-center'>{sample.size || '-'}</td>
                </tr>
              ))}
              <tr>
                <td className='border h-full'></td>
                <td className='border'></td>
                <td className='border'></td>
                <td className='border'></td>
                <td className='border-b'></td>
                <td className='border-x border-b'></td>
              </tr>
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  )
}
function QrAndUrl(props: { sampleInfo: SamplesResponseType }) {
  const { sampleInfo } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const openModal = useCallback(() => { setIsModalOpen(true) }, [])
  const closeModal = useCallback(() => { setIsModalOpen(false) }, [])

  const onQrDownload = useCallback(async (id: string) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${id}/qr-code/download`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      })

      if (!res.ok) throw new Error('ダウンロード失敗')

      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `qr_${id}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('DOWNLOAD ERROR', error)
    }
  }, [])

  const onCopyUrl = useCallback(async () => {
    if (inputRef.current) {
      inputRef.current.select()
      try {
        await navigator.clipboard.writeText(inputRef.current.value)
      } catch (error) {
        console.error('リンクのコピーに失敗しました。')
      }
      openModal()
    }
  }, [openModal])

  return (
    <div className="w-full max-w-[590px] h-[686px] min-w-[250px] flex flex-col justify-between bg-white rounded-[10px] shadow-[0_2px_2px_0_rgba(0,0,0,0.25)] pt-3 px-[15px] pb-8 desktop-xxl:min-w-[590px]">
      <div className="flex flex-col items-center">
        <div className='flex items-center w-full'>
          <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
          <span className='text-[#3E3E3E] text-[16px]'>
            QR CODE
          </span>
        </div>
        {sampleInfo.qr_code ? (
          <>
            <div className='flex items-center justify-center w-full p-8 border-[2.5px] border-[#D9D9D9] rounded-[20px] mt-[8px] max-w-[437px]'>
              <img src={sampleInfo.qr_code} alt='qrcode' className='w-full h-auto max-w-[332px]' />
            </div>
            <div className='flex items-center w-full h-[40px] border-[2.5px] border-[#D9D9D9] rounded-[10px] mt-[8px] max-w-[437px]'>
              <input className='text-[16px] h-full w-full rounded-[10px] px-[18px] bg-white' ref={inputRef} value={sampleInfo.url} readOnly />
            </div>
          </>
        ) : (
          <>
            <div className='flex items-center justify-center w-full p-8 border-[2.5px] border-[#D9D9D9] rounded-[20px] mt-[8px] aspect-square max-w-[437px] max-h-[405px]'>
            </div>
            <div className='flex items-center w-full h-[40px] border-[2.5px] border-[#D9D9D9] rounded-[10px] mt-[8px] max-w-[437px]'>
              <input className='text-[16px] h-full w-full rounded-[10px] px-[18px] bg-white' ref={inputRef} value="" readOnly />
            </div>
          </>
        )}
        <div className='flex w-full max-w-[437px] justify-between items-center mt-[28px] max-tablet-sm:flex-col max-tablet-sm:items-end'>
          <div className='relative group'>
            <span className={[
              'flex',
              'items-center',
              'justify-center',
              'w-[250px]',
              'h-[92px]',
              'px-[19px]',
              'absolute',
              'bg-white',
              'text-[14px]',
              '-translate-x-1/2',
              'left-1/2',
              'bottom-[-100px]',
              'opacity-0',
              'group-hover:opacity-100',
              'transition',
              "pointer-events-none"
            ].join(" ")} style={{ boxShadow: '0px 0px 4px 0px #00000040' }}>
              コードをスキャンするには、スマートフォンのQRスキャンアプリまたはカメラアプリを使用してください。
            </span>
            <img src={infoIcon} alt='info' className='w-6 h-6 cursor-help' />
          </div>
          <div className='space-x-[9px] flex flex-col items-end space-y-[10px] 2xl:flex-row max-desktop-sm:flex-row'>
            <button
              className={`w-[105px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] text-[16px] font-semibold hover:bg-[#C5C7FF] transition-colors ${!sampleInfo.url ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={onCopyUrl}
            >
              コピー
            </button>
            <button
              className={`w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] text-[16px] font-semibold hover:bg-[#C5C7FF] transition-colors ${!sampleInfo.qr_code ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={() => onQrDownload(sampleInfo.id)}
            >
              ダウンロード
            </button>
          </div>
        </div>
      </div>
      <MessageModal isOpen={isModalOpen} onClose={closeModal} />
    </div>
  )
}

function MessageModal({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='flex items-center justify-center w-[414px] h-[130px]'>
      <img src={checkIcon} alt='check' className='w-6 h-6 mr-[5px]' />
      <span className='text-[14px]'>リンクをコピーしました。</span>
    </Modal>
  )
}

function SamplePhotos(props: { sampleInfo: SamplesResponseType, isMapReady: boolean }) {
  const { sampleInfo, isMapReady } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { getColor, getDisplayIndex } = useTableContext()
  const [containerWidth, setContainerWidth] = useState(0)
  const [isSampleCanvasLoading, setIsSampleCanvasLoading] = useState(false)

  const ecImagesSorted = useMemo(
    () => (sampleInfo.sampleEcImages || []).slice().sort((a, b) => a.image_index - b.image_index),
    [sampleInfo.sampleEcImages]
  )

  const getCanvasSizes = useCallback((imgUrl: string) => {
    if (!imgUrl || !containerRef.current) return null
    return loadPreviewImage(imgUrl, { crossOrigin: 'anonymous' }).then(img => {
      const aspect = img.width / img.height
      const w = containerWidth
      const h = Math.floor(w / aspect)
      const dpr = window.devicePixelRatio || 1
      return { img, w, h, dpr }
    })
  }, [containerWidth])

  // ── リサイズ監視 ──
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    setContainerWidth(el.clientWidth)
    const ro = new ResizeObserver(entries => {
      const w = Math.floor(entries[0].contentRect.width)
      setContainerWidth(prev => (prev !== w ? w : prev))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !containerRef.current || !isMapReady || !containerWidth) {
      setIsSampleCanvasLoading(false)
      return
    }

    const imgUrl = sampleInfo.sampleImages?.[0]?.url
    if (!imgUrl) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      setIsSampleCanvasLoading(false)
      return
    }

    setIsSampleCanvasLoading(true)
    let canceled = false
    const sizesPromise = getCanvasSizes(imgUrl)
    const ctx = canvas.getContext('2d')

    if (!sizesPromise || !ctx) {
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      setIsSampleCanvasLoading(false)
      return
    }

    sizesPromise.then(({ img, w, h, dpr }) => {
      if (canceled) return
      canvas.width = w * dpr
      canvas.height = h * dpr
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.resetTransform()
      ctx.scale(dpr, dpr)
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      ctx.clearRect(0, 0, w, h)
      ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, w, h)

      const scaleX = w / img.width
      const scaleY = h / img.height

      const activeMeasurements = sampleInfo.sampleMeasurements
        .filter(m => !m.is_deleted)
        .sort((a, b) => a.measure_index - b.measure_index)

      activeMeasurements.forEach(m => {
        if (m.result_point.length < 2) return

        const px = m.result_point.map(p => ({
          x: p.x * scaleX,
          y: p.y * scaleY,
        }))
        const displayIdx = getDisplayIndex(m.measure_index)
        const color = getColor(m.measure_index)

        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(px[0].x, px[0].y)
        px.slice(1).forEach(p => ctx.lineTo(p.x, p.y))
        ctx.stroke()

        const mid = { x: (px[0].x + px[1].x) / 2, y: (px[0].y + px[1].y) / 2 }
        const segPx = Math.hypot(px[1].x - px[0].x, px[1].y - px[0].y)
        const fontSize = Math.min(20, Math.max(10, segPx * 0.06))
        const r = fontSize * 0.55

        ctx.beginPath()
        ctx.fillStyle = '#FFF'
        ctx.arc(mid.x, mid.y, r, 0, Math.PI * 2)
        ctx.fill()

        ctx.fillStyle = '#000'
        ctx.font = `${fontSize}px sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(formatNumber(displayIdx), mid.x, mid.y)
      })
    }).catch(() => {
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    }).finally(() => {
      if (!canceled) {
        setIsSampleCanvasLoading(false)
      }
    })

    return () => {
      canceled = true
    }
  }, [sampleInfo, getCanvasSizes, getColor, getDisplayIndex, isMapReady, containerWidth])

  return (
    <div className="bg-white w-full max-w-[590px] min-w-[250px] h-[686px] rounded-[10px] shadow-[0_2px_2px_0_rgba(0,0,0,0.25)] pt-3 px-[15px] pb-8 desktop-xxl:min-w-[590px]">
      <div className='flex items-center w-full mb-[20px]'>
        <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
        <span className='text-[#3E3E3E] text-[16px]'>
          採寸/EC 画像
        </span>
      </div>
      <div ref={containerRef} className="relative bg-white w-full overflow-hidden h-[338px] p-[5px] mb-[20px] rounded-[10px] flex items-center justify-center border-[2px] border-gray-100">
        {sampleInfo.sampleImages && sampleInfo.sampleImages.length > 0 ? (
          <canvas ref={canvasRef} />
        ) : (
          <div className="w-full h-full" />
        )}
        {isSampleCanvasLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <ReactLoading type="spin" color="#545899" width={36} height={36} />
          </div>
        )}
      </div>
      <div className="mt-[20px]">
        <div className="relative max-h-[222px] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 max-desktop-sm:grid-cols-3 gap-x-[5px] gap-y-[8px]">
            {sampleInfo.sampleEcImages && sampleInfo.sampleEcImages.length > 0 ? (
              ecImagesSorted.map((ecImage, index) => (
                <div
                  key={index}
                  className="relative w-full max-w-[183px] min-h-[107px] aspect-[183/107] bg-white rounded-[5px] overflow-hidden flex items-center justify-center border-[2px] border-gray-100"
                >
                  <ImageWithLoading
                    data-ec-image={ecImage.image_index}
                    src={ecImage.thumbnail_url}
                    alt={`ec_image_${index}`}
                    className="object-contain w-full h-full transition-opacity duration-200"
                    loadingFallback={
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <ReactLoading type="spin" color="#545899" width={24} height={24} />
                      </div>
                    }
                  />
                </div>
              ))
            ) : (
              Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="w-full max-w-[183px] aspect-[183/107] rounded-[5px] border-[2px] border-gray-100"
                  style={{ minHeight: '107px' }} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
